/**
 * Advisory location proof for arrival actions.
 *
 * Honest trust model: a browser cannot cryptographically prove real presence,
 * and advance_flow (server-side) has no access to the device's true location —
 * it can only store whatever the client submits. So this is an ADVISORY signal:
 * an honesty nudge + evidence for later dispute arbitration, never a hard gate.
 * Capture is best-effort and NEVER blocks the action — a denied/unavailable
 * result still resolves (with a `source` marker) so mark_here always proceeds.
 *
 * Stored shape (jsonb): { lat, lng, accuracy_m, captured_at, source }
 *   source: 'browser'  — real coordinates
 *           'denied' | 'unavailable' | 'timeout' | 'unsupported' — no coords
 *
 * On a Capacitor native build it uses the @capacitor/geolocation plugin (which
 * handles the OS permission prompt); on web it uses navigator.geolocation.
 */
import { Capacitor } from '@capacitor/core';

const stamp = (extra) => ({ captured_at: new Date().toISOString(), ...extra });

// Native path (Android/iOS via Capacitor). Never throws — resolves a marker.
async function captureNative(timeoutMs) {
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    let perm = await Geolocation.checkPermissions();
    if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
      perm = await Geolocation.requestPermissions();
    }
    if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
      return stamp({ source: 'denied' });
    }
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: timeoutMs });
    const c = (pos && pos.coords) || {};
    return stamp({
      source: 'browser',
      lat: c.latitude,
      lng: c.longitude,
      accuracy_m: c.accuracy != null ? Math.round(c.accuracy) : null,
    });
  } catch (e) {
    return stamp({ source: 'unavailable' });
  }
}

export function captureGeoproof({ timeoutMs = 5000 } = {}) {
  if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    return captureNative(timeoutMs);
  }

  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(stamp({ source: 'unsupported' }));
      return;
    }

    let settled = false;
    const done = (v) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos.coords || {};
        done(
          stamp({
            source: 'browser',
            lat: c.latitude,
            lng: c.longitude,
            accuracy_m: c.accuracy != null ? Math.round(c.accuracy) : null,
          })
        );
      },
      (err) => {
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        const source = err && err.code === 1 ? 'denied' : err && err.code === 3 ? 'timeout' : 'unavailable';
        done(stamp({ source }));
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 }
    );

    // Hard backstop in case the browser never calls either callback.
    setTimeout(() => done(stamp({ source: 'timeout' })), timeoutMs + 500);
  });
}
